import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { addressObject, upperFirstChar } from 'src/helpers/general';
import { cardTypes } from 'src/helpers';
import { postcodeValidator } from 'postcode-validator';
import constants from 'src/constants';
import * as ibantools from 'ibantools';
import semver from 'semver';
import LANG_CONST from '../lang/const';

const displayNameRegexp = /^[\p{L}\p{M}0-9._\s@#&'\u2019-]+$/u;
const personalNameRegexp = /^[\p{L}\p{M}][\p{L}\p{M} .'\u2019-]*$/u;

const personalNameRule = {
  type: 'string',
  required: true,
  regexp: personalNameRegexp,
  minLength: 1,
  maxLength: 50,
  message: 'Use letters, spaces, apostrophes, hyphens, or periods.'
};

const fullNameRule = {
  type: 'string',
  required: true,
  regexp: personalNameRegexp,
  minLength: 2,
  maxLength: 100,
  message: 'Use letters, spaces, apostrophes, hyphens, or periods.'
};

const rules = {
  defaultMessages: {
    regular: 'invalid',
    tooShort: 'too short',
    required: 'required',
    minLength: '{{val}} min',
    maxLength: '{{val}} max'
  },
  number: {
    type: 'number'
  },
  string: {
    type: 'string'
  },
  array: {
    type: 'array'
  },
  boolean: {
    type: 'boolean'
  },
  object: {
    type: 'object'
  },
  required: {
    type: 'any',
    required: true
  },
  journeyTitle: {
    type: 'string',
    required: true,
    maxLength: 255
  },
  baseId: {
    type: 'string',
    required: true
  },
  journeyDescription: {
    type: 'string',
    required: true,
    maxLength: 1000
  },
  journeyIcon: {
    type: 'any',
    required: true
  },
  journeyCover: {
    type: 'any',
    required: true
  },

  //login
  username: {
    type: 'string',
    regexp: /^[a-z][a-z0-9.-]+$/i,
    exclude: ['page', 'markup', 'user'],
    minLength: 2,
    maxLength: 50,
    message:
      'Your username must start with a letter, be 2–50 characters, and can only include letters (a-z), digits (0-9), dots (.) or hyphens (-)',
    lowercase: true,
    required: true
  },
  password: {
    type: 'string',
    minLength: 8,
    maxLength: 50,
    regexp: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
    tooShortMessage: 'Password must be 8+ characters with upper, lower, and numbers.',
    message:
      'Password must be 8–50 characters with at least one uppercase letter, one lowercase letter, and one number'
  },
  confirmPassword: {
    regexp: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
    message: 'Password must be 8+ characters with upper, lower, and numbers.'
  },
  newPassword: 'password',
  passwordConfirm: {
    type: 'array',
    fn: (passwords) => passwords[0] === passwords[1],
    message: 'Passwords does not match'
  },

  cardNumber: {
    type: 'string',
    required: true,
    fn: (v) => {
      return !!cardTypes.find({ cardNumber: v });
    }
  },
  cardHolder: {
    type: 'string',
    required: true
  },
  cardExpire: {
    type: 'string',
    required: true
  },
  cardExpireMonth: {
    type: 'string',
    required: true
  },
  cardExpireYear: {
    type: 'string',
    required: true
  },
  cardCvv: {
    type: 'string',
    required: true
  },
  //user
  name: {
    type: 'string',
    required: true,
    regexp: displayNameRegexp,
    minLength: 2,
    maxLength: 80,
    message: 'Use letters, numbers, spaces, and common display-name punctuation.'
  },
  personalName: { ...personalNameRule },
  firstName: { ...personalNameRule },
  lastName: { ...personalNameRule },
  fullName: { ...fullNameRule },
  fullname: { ...fullNameRule },
  gender: {
    type: 'string'
  },
  zipcode: {
    type: 'string',
    fn: (v, { body }) => {
      try {
        return !v || postcodeValidator(v, addressObject(body).countryCode);
      } catch {
        return false;
      }
    }
  },
  bankZipcode: {
    type: 'string',
    fn: (v, { body }) => {
      try {
        return (
          !v ||
          postcodeValidator(
            v,
            addressObject({
              countryAddress: body.bankCountryAddress,
              stateAddress: body.bankStateAddress,
              cityAddress: body.bankCityAddress
            }).countryCode
          )
        );
      } catch {
        return false;
      }
    }
  },
  birthday: {
    required: true,
    type: 'number'
  },
  email: {
    type: 'string',
    maxLength: 50,
    noMinLength: true,
    message: 'Must be valid email address',
    fn: (email) => {
      const emailRegex = new RegExp(/^[A-Za-z0-9_!#$%&'*+/?=`{|}~^.-]+@[A-Za-z0-9.-]+$/, 'gm');

      return !email || emailRegex.test(email);
    }
  },
  emailAddress: {
    type: 'string',
    minLength: 3,
    maxLength: 50,
    message: 'Must be valid email address',
    required: true,
    fn: (email) => {
      const emailRegex = new RegExp(/^[A-Za-z0-9_!#$%&'*+/?=`{|}~^.-]+@[A-Za-z0-9.-]+$/, 'gm');

      return !email || emailRegex.test(email);
    }
  },
  emails: {
    type: 'array',
    required: true,
    fn: (emails) => {
      const emailRegex = new RegExp(/^[A-Za-z0-9_!#$%&'*+/?=`{|}~^.-]+@[A-Za-z0-9.-]+$/, 'gm');
      const invalid = { length: 0 };
      const msg = [];
      if (emails.length === 0) return 'Email is required.';
      emails.forEach((email) => {
        if (!(
          typeof email === 'string' &&
          email.length >= 3 &&
          email.length <= 50 &&
          emailRegex.test(email)
        )) {
          msg.push(email + ' is invalid email address');
          invalid.length++;
        }
      });
      return invalid.length === 0 ? true : msg.join('\n');
    }
  },
  avatar: {
    type: 'string'
  },
  specialist: {
    required: true,
    type: 'array',
    maxLength: 7
  },
  cityAddress: {
    type: 'string'
  },
  city: {
    type: 'string',
    required: true
  },
  hospital: {
    type: 'string',
    required: true
  },
  countryAddress: {
    type: 'string'
  },
  certificateTitle: {
    type: 'string',
    required: true
  },
  promotionYear: {
    type: 'any',
    required: true
  },
  specialty: {
    type: 'string',
    required: true
  },
  certificateFiles: {
    type: 'array',
    required: true
  },
  certificateFiles2: {
    type: 'array',
    fn: (certificates) => {
      const invalid = { length: 0 };
      certificates.forEach((certificate, i) => {
        if (!(typeof certificate.title === 'string' && certificate.title.length > 0)) {
          invalid['certificate_title_' + i] = 'Certificate Title is required';
          invalid.length++;
        }
      });
      return invalid.length === 0 ? true : invalid;
    }
  },
  managerAccess: {
    type: 'array',
    fn: (data) =>
      !data.includes((v) => constants.general.manager_access.includes((item) => item.value !== v))
  },
  description: {
    type: 'string',
    maxLength: 3000,
    required: true
  },
  hospitalName: {
    type: 'string'
  },
  hospitalAddress: {
    type: 'string'
  },
  speciality: {
    type: 'string',
    required: true
  },
  doctorType: {
    type: 'string',
    required: true
  },
  institution: {
    type: 'string',
    required: true
  },
  yearOfStarting: {
    type: 'number',
    required: true
  },
  treatments: {
    type: 'array',
    fn: (treatments) => {
      const invalid = { length: 0 };
      treatments.forEach((treatment, i) => {
        if (!(
          typeof treatment.name === 'string' &&
          treatment.name.length > 2 &&
          treatment.name.length < 200
        )) {
          invalid['treatment_name_' + i] = 'Name is invalid';
          invalid.length++;
        }
        if (!(
          typeof treatment.fee === 'number' &&
          treatment.fee >= 0 &&
          treatment.fee < 1000000000
        )) {
          invalid['treatment_fee_' + i] = 'Fee is invalid';
          invalid.length++;
        }
      });
      return invalid.length === 0 ? true : invalid;
    }
  },
  address: {
    type: 'string'
  },
  phoneNumber: {
    type: 'string',
    required: true,
    message: 'Wrong phone number format',
    fn: (v, { body }) => {
      try {
        const countryCode = body?.countryCode ? body.countryCode : parsePhoneNumber(v).country;
        return (
          !v ||
          isValidPhoneNumber(v, countryCode) ||
          v.substring(0, 3) == '+47' ||
          v.substring(0, 3) == '+69'
        ); // +47 used for virtual users;
      } catch {
        return false;
      }
    }
  },
  phone: {
    type: 'string',
    message: 'Wrong phone number format',
    fn: (v, { body }) => {
      try {
        if (!v) return true;
        const countryCode = body?.countryCode ? body.countryCode : parsePhoneNumber(v).country;
        return (
          !v ||
          isValidPhoneNumber(v, countryCode) ||
          v.substring(0, 3) == '+47' ||
          v.substring(0, 3) == '+69'
        ); // +47 used for virtual users;
      } catch {
        return false;
      }
    }
  },
  phoneNumberCode: {
    type: 'string',
    required: true
  },
  charge: {
    type: 'object',
    fn: (charge) => {
      let error = {};
      let hasError = false;
      let setTypes = ['first', 'followup', 'chatTicket'],
        feeTypes = [
          { n: 'office', l: 'Office' },
          { n: 'virtual', l: 'Virtual' }
          // { n: 'chatTicket', l: 'Chat Ticket' }
        ];
      let chatTicketFeeTypes = [
        { n: 'price', l: 'Price' },
        { n: 'duration', l: 'Duration' },
        { n: 'messagesCount', l: 'No of messages' },
        { n: 'extraMessage', l: 'Extra message' }
      ];

      setTypes.forEach((type) => {
        const fees = charge[type];
        if (type == 'chatTicket' && !fees.paid) return;
        let fTypes = type == 'chatTicket' ? chatTicketFeeTypes : feeTypes;
        fTypes.forEach((feeType) => {
          if (fees[feeType.n] || fees[feeType.n] === 0) {
            if (typeof fees[feeType.n] != 'number' || isNaN(fees[feeType.n])) {
              error[[charge.id, type, feeType.n].join('_')] = feeType.l + ' is invalid.';
              hasError = true;
            }
          } else {
            console.warn('invalid', [charge.id, type, feeType.n].join('_'), fees);
            error['required_all'] = 'All fields are required.';
            hasError = true;
          }
        });
      });
      return hasError ? error : true;
    }
  },
  charges: {
    type: 'array',
    fn: (charges) => {
      let error = {};
      let hasError = false;
      let setTypes = ['first', 'followup', 'chatTicket'],
        feeTypes = [
          { n: 'office', l: 'Office' },
          { n: 'virtual', l: 'Virtual' }
          // { n: 'chatTicket', l: 'Chat Ticket' }
        ];
      let chatTicketFeeTypes = [
        { n: 'price', l: 'Price' },
        { n: 'duration', l: 'Duration' },
        { n: 'messagesCount', l: 'No of messages' },
        { n: 'extraMessage', l: 'Extra message' }
      ];

      charges.forEach((charge) =>
        setTypes.forEach((type) => {
          const fees = charge[type];
          if (type == 'chatTicket' && !fees.paid) return;
          let fTypes = type == 'chatTicket' ? chatTicketFeeTypes : feeTypes;
          fTypes.forEach((feeType) => {
            if (fees[feeType.n] || fees[feeType.n] === 0) {
              if (typeof fees[feeType.n] != 'number' || isNaN(fees[feeType.n])) {
                error[[charge.id, type, feeType.n].join('_')] = feeType.l + ' is invalid.';
                hasError = true;
              }
            } else {
              console.warn('invalid', [charge.id, type, feeType.n].join('_'));
              error['required_all'] = 'All fields are required.';
              hasError = true;
            }
          });
        })
      );
      return hasError ? error : true;
    }
  },
  hospitalSchedule: {
    type: 'object',
    fn: (schedule) => {
      let error = {};
      let hasError = false;
      Object.keys(schedule).forEach((date) => {
        const item = schedule[date];
        let { times = [], isOpen } = item;
        if (isOpen) {
          times.forEach((time) => {
            if (time.startTime && time.endTime && time.startTime >= time.endTime) {
              error[date] = 'Start Time should be greater then End Time';
              hasError = true;
            }
          });
        }
      });
      return hasError ? error : true;
    }
  },
  slots: {
    type: 'array',
    fn: (slots) => {
      const invalid = { length: 0 };

      const hasConflict = (start, end, index) =>
        slots.find(
          (slot, i) =>
            i !== index &&
            !!slot.startDate &&
            !!slot.endDate &&
            slot.endDate > start &&
            slot.startDate < end
        );
      slots.forEach((item, i) => {
        if (!item.startDate || !item.endDate) {
          invalid['slot_' + i] = 'Time is required';
          invalid.length++;
        } else if (item.startDate >= item.endDate) {
          invalid['slot_' + i] = LANG_CONST.validation.start_end_time;
          invalid.length++;
        } else if (hasConflict(item.startDate, item.endDate, i)) {
          invalid['slot_' + i] = LANG_CONST.validation.conflict_slot_time;
          invalid.length++;
        } else if ((item.endDate - item.startDate) / 60000 < 20) {
          invalid['slot_' + i] = LANG_CONST.validation.time_slot_min;
          invalid.length++;
        }
        if (item.type === 'patient') {
          if (!item.patientId) {
            invalid['patient_' + i] = 'Patient is required';
            invalid.length++;
          }
        }
        if (!item.appointmentTypes || item.appointmentTypes.length === 0) {
          invalid['appointment_types_' + i] = 'Type is required';
          invalid.length++;
        }
        if (item.appointmentTypes?.indexOf('in_office') > -1 && !item.hospital) {
          invalid['hospital_' + i] = 'Hospital is required';
          invalid.length++;
        }
      });
      return invalid.length === 0 ? true : invalid;
    }
  },
  question: {
    type: 'string',
    required: true,
    maxLength: 255
  },
  vitalInfo: {
    type: 'object'
  },

  // paymentDetails
  iban: {
    type: 'string',
    required: true,
    fn: (iban) => {
      if (!iban) {
        return true;
      }

      return ibantools.isValidIBAN(iban);
    }
  },
  revolutId: {
    type: 'string'
  },
  swift: {
    type: 'string',
    fn: (swift) => {
      if (!swift) {
        return true;
      }

      return ibantools.isValidBIC(swift);
    }
  },

  //book appointment
  disease: {
    type: 'string',
    required: true
  },
  symptoms: {
    type: 'string',
    required: true
  },
  answers: {
    type: 'array',
    fn: (answers) => {
      const invalid = { length: 0 };
      answers.forEach((item, i) => {
        if (typeof item.answer !== 'string' || item.answer.length < 1) {
          invalid['answer_' + i] = 'Answer is required';
          invalid.length++;
        }
      });
      return invalid.length === 0 ? true : invalid;
    }
  },

  //treatment
  medicines: {
    type: 'array',
    required: true,
    fn: (medicines) => {
      const invalid = { length: 0 };
      medicines.forEach((medicine, i) => {
        if (typeof medicine.name !== 'string' || medicine.name.length < 1) {
          invalid['medicine_name_' + i] = 'Name is required';
          invalid.length++;
        }
        if (medicine.name.length > 200) {
          invalid['medicine_name_' + i] = 'Name is too long.';
          invalid.length++;
        }
        if (medicine.times.length === 0) {
          invalid['medicine_times_' + i] = 'Timing is required';
          invalid.length++;
        }
      });
      return invalid.length === 0 ? true : invalid;
    }
  },
  recommendations: {
    type: 'array',
    fn: (recommendations) => {
      const invalid = { length: 0 };
      recommendations.forEach((recommendation, i) => {
        ['type', 'detail'].forEach((key) => {
          if (typeof recommendation[key] !== 'string' || recommendation[key].length < 1) {
            invalid['recommendation_' + key + '_' + i] = upperFirstChar(key) + ' is required';
            invalid.length++;
          }
          if (recommendation[key].length > 200) {
            invalid['recommendation_' + key + '_' + i] = upperFirstChar(key) + ' is too long';
            invalid.length++;
          }
        });
      });
      return invalid.length === 0 ? true : invalid;
    }
  },
  diagnosis: {
    type: 'string',
    required: true
  },
  note: {
    type: 'string'
  },
  recommendation: {
    type: 'string',
    required: true
  },

  //note
  title: {
    type: 'string',
    minLength: 3,
    maxLength: 240
  },
  noteId: {
    type: 'string',
    required: true
  },

  // invoice
  invoiceNo: {
    type: 'number',
    required: true
  },
  serie: {
    type: 'string',
    required: true
  },
  invoiceDate: {
    type: 'number',
    required: true
  },
  patientId: {
    type: 'number',
    required: true
  },
  invoiceDetails: {
    type: 'array',
    fn: (invoiceDetails) => {
      const invalid = { length: 0 };
      invoiceDetails.forEach((invoiceDetail, i) => {
        ['description'].forEach((key) => {
          if (typeof invoiceDetail[key] !== 'string' || invoiceDetail[key].length < 1) {
            invalid['invoiceDetail_' + key + '_' + i] = upperFirstChar(key) + ' is required';
            invalid.length++;
          }
          if (invoiceDetail[key].length > 200) {
            invalid['invoiceDetail_' + key + '_' + i] = upperFirstChar(key) + ' is too long';
            invalid.length++;
          }
        });
        ['price', 'vat'].forEach((key) => {
          if (typeof invoiceDetail[key] !== 'number' || !invoiceDetail[key]) {
            invalid['invoiceDetail_' + key + '_' + i] = upperFirstChar(key) + ' is required';
            invalid.length++;
          }
        });
      });
      return invalid.length === 0 ? true : invalid;
    }
  },

  //feedback
  isImproved: {
    type: 'boolean'
  },
  improvedComment: {
    type: 'string'
  },
  isEffect: {
    type: 'boolean'
  },
  effectComment: {
    type: 'string'
  },

  userId: {
    type: 'number'
  },
  text: {
    type: 'string'
  },
  timestamp: {
    type: 'number',
    min: 0
  },
  website: {
    type: 'string',
    regexp: /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[-\w._~:/?#[\]@!$&'()*+,;=.]+$/
  },
  token: {
    type: 'string',
    minLength: 3,
    maxLength: 2550
  },
  sid: {
    type: 'string',
    minLength: 3,
    maxLength: 255
  },
  url: {
    type: 'string',
    minLength: 3,
    maxLength: 1255,
    regexp:
      /^(?:(?:(?:https?|ftp):)?\/\/)?(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i,
    message: 'The url should be like "https://www.foxvoo.com" or "www.foxvoo.com" or "foxvoo.com"'
  },
  amount: {
    type: 'number',
    min: 0,
    max: 999999999
  },
  key: {
    type: 'string',
    minLength: 1,
    maxLength: 30
  },
  _id: {
    type: 'objectId',
    objectId: true
  },
  skip: {
    type: 'number',
    min: 0,
    max: 999999999
  },
  limit: {
    type: 'number',
    min: 1,
    max: 999999999
  },
  imageId: {
    type: 'number',
    min: 0
  },
  urlName: {
    type: 'string',
    regexp: /^[a-z0-9]([a-z0-9]+[_.-]?)*[a-z0-9]$/,
    exclude: [
      'page',
      'markup',
      'user',
      'login',
      'signup',
      'notifications',
      'topics',
      'forgot-password',
      'reset-password'
    ],
    minLength: 2,
    maxLength: 50,
    message: 'Only alphanumeric characters are allowed a-z + _.- + 0-9',
    lowercase: true
  },
  //room
  roomId: {
    type: 'number'
  },
  roomName: {
    type: 'string',
    regexp: /^[a-z0-9._@# ]+$/i,
    minLength: 3,
    maxLength: 30
  },
  roomKeyword: {
    type: 'string',
    regexp: /^[a-z0-9._\-@#\s]+$/i,
    minLength: 2,
    maxLength: 30
  },
  roomKeywords: {
    type: 'array',
    items: 'roomKeyword',
    minLength: 0,
    maxLength: 100
  },
  keyword: {
    type: 'string',
    regexp: /^[a-z0-9._\-\s]+$/i,
    minLength: 2,
    maxLength: 30,
    message: 'Only alphanumeric characters are allowed A-Z+0-9 -'
  },
  keywords: {
    type: 'array',
    items: 'keyword',
    minLength: 0,
    maxLength: 100,
    message: 'Only alphanumeric characters are allowed A-Z+0-9 -'
  },
  roomDescription: {
    type: 'string',
    minLength: 0,
    maxLength: 1000
  },
  roomOrder: {
    type: 'string',
    oneOf: ['live board', 'chat', 'message board']
  },
  topicTitle: {
    type: 'string',
    minLength: 3,
    maxLength: 256
  },
  contactId: {
    type: 'number'
  },
  analyseTitle: {
    type: 'string',
    required: true
  },
  analyseFile: {
    type: 'files',
    required: true
  },
  timelineTitle: {
    type: 'string',
    required: true
  },
  timelineDescription: {
    type: 'string',
    required: true
  },
  timelineFile: {
    type: 'object',
    required: true
  },
  treatmentId: {
    type: 'number',
    required: true
  },
  treatmentName: {
    required: true,
    type: 'string'
  },
  treatmentFee: {
    required: true,
    type: 'number'
  },
  bookDate: {
    type: 'object',
    required: true
  },
  bookTime: {
    type: 'string',
    required: true
  },
  currency: {
    type: 'string',
    oneOf: ['USD', 'EUR', 'RON'],
    required: true
  },
  semver: {
    type: 'string',
    required: true,
    fn: (val) => semver.valid(val)
  }
};

export default rules;
